import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Card, MenuItem, Select, FormControl, Grid } from '@mui/material';
import { useState } from 'react';
import { ICandidateSubmissionSearchFilterVM } from 'wisestep-api';
import {
  IUserDTO,
  ScrollableButtonTabs,
  WsChip,
  WsChipSize,
  WsChipVariants,
  WsColors,
} from 'wisestep-base';

import { useUserContext } from '../../../../context';
import { getDefaultQueueTab, getDefaultSelectedSubmissionType } from '../../utils/Helper';

const submissionsTypes = [
  {
    label: 'All Submissions',
    value: 'all_submissions',
  },
  {
    label: 'My Submissions',
    value: 'my_submissions',
  },
  {
    label: 'My Queue',
    value: 'my_queue',
  },
];

interface ISubmissionHeaderProps {
  searchQuery: ICandidateSubmissionSearchFilterVM;
  getUpdateFiltersFromHeaders: (data) => void;
  pendingCount?: number;
}

const styles = {
  select: {
    fontWeight: 'bold',
    fontSize: '18px',
    minWidth: 170,
    maxHeight: 30,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
    '& .MuiSelect-select': { fontWeight: 'bold' },
    color: (isDropDownOpen) => (isDropDownOpen ? WsColors.TheBlues500 : WsColors.GenericRichBlack),
  },
  menuItem: {
    fontWeight: '500',
    fontSize: '14px',
  },
  tabs: {
    '& .MuiTabs-indicator': { display: 'none' },
    '& .Mui-selected': { borderBottom: 'none' },
  },
  tab: {
    '&.Mui-selected': {
      backgroundColor: WsColors.TheBlues500,
      color: `${WsColors.White}!important`,
      borderRadius: '12px',
      minHeight: '10px',
    },
    minHeight: '10px',
    margin: '0 5px',
  },
};

const Header = ({
  searchQuery,
  getUpdateFiltersFromHeaders,
  pendingCount,
}: ISubmissionHeaderProps) => {
  const loggedInUser = useUserContext().userDTO as IUserDTO;

  const [selectedType, setSelectedType] = useState<string>(
    getDefaultSelectedSubmissionType(searchQuery, loggedInUser?.email ?? '')
  );
  const [isDropDownOpen, setIsDropDownOpen] = useState<boolean>(false);
  const [myQueueTab, setMyQueueTab] = useState<number>(
    selectedType === 'my_queue' ? getDefaultQueueTab(searchQuery) : 0
  );

  const handleSubmissionsTypeChange = (event) => {
    setIsDropDownOpen(false);
    const type = event.target.value as string;
    setSelectedType(type);
    const updatedQuery = { ...searchQuery };
    setMyQueueTab(0);
    const submittedJobIndex = updatedQuery.filters.findIndex(
      (filter) => filter.filterName === 'SUBMITTED_TEAM_MEMBER_ASSIGNED_JOB'
    );

    if (type === 'my_submissions' && loggedInUser?.email) {
      const newFilter = {
        filterName: 'SUBMITTED_TEAM_MEMBER_ASSIGNED_JOB',
        values: [loggedInUser?.email],
        fields: {},
      };
      if (submittedJobIndex === -1) {
        updatedQuery.filters.push(newFilter);
      }
    } else if (submittedJobIndex !== -1) {
      updatedQuery.filters.splice(submittedJobIndex, 1);
    }

    const myQueueIndex = updatedQuery.filters.findIndex(
      (filter) => filter.filterName === 'MY_QUEUE'
    );

    if (type === 'my_queue' && myQueueIndex === -1) {
      const newFilter = {
        filterName: 'MY_QUEUE',
        values: myQueueTab === 0 ? ['PENDING'] : ['ASSIGNED'],
        fields: {},
      };
      updatedQuery.filters.push(newFilter);
    } else if (myQueueIndex !== -1) {
      updatedQuery.filters.splice(myQueueIndex, 1);
    }

    getUpdateFiltersFromHeaders(updatedQuery);
  };

  const handleMyQueueTabs = (index: number) => {
    setMyQueueTab(index);

    const updatedQuery = { ...searchQuery };

    const filterIndex = updatedQuery.filters.findIndex(
      (filter) => filter.filterName === 'MY_QUEUE'
    );
    const filterValue = index === 0 ? ['PENDING'] : ['ASSIGNED'];
    if (filterIndex !== -1) {
      updatedQuery.filters[filterIndex] = {
        ...updatedQuery.filters[filterIndex],
        values: filterValue,
      };
    } else {
      updatedQuery.filters.push({
        filterName: 'MY_QUEUE',
        values: filterValue,
        fields: {},
      });
    }
    getUpdateFiltersFromHeaders(updatedQuery);
  };
  const handleOpen = () => setIsDropDownOpen(true);
  const handleClose = () => setIsDropDownOpen(false);

  return (
    <Card sx={{ minHeight: 10 }}>
      <Grid container p={1}>
        <Grid>
          <FormControl variant="outlined">
            <Select
              id="submissions_types"
              name="submissions_types"
              value={selectedType}
              onChange={handleSubmissionsTypeChange}
              IconComponent={KeyboardArrowDownIcon}
              MenuProps={{ onClose: handleClose, open: isDropDownOpen }}
              onOpen={handleOpen}
              sx={styles.select}
            >
              {submissionsTypes.map((type, index) => (
                <MenuItem key={index} value={type.value} sx={styles.menuItem}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item>
          <WsChip
            value={`Pending queue: ${pendingCount}`}
            size={WsChipSize.MEDIUM}
            variant={WsChipVariants.INVALID}
          />
        </Grid>
        {selectedType === 'my_queue' && (
          <Grid xs={12} ml={-4} container spacing={2}>
            <Grid item xs={11.5}>
              <ScrollableButtonTabs
                scrollList={[{ tabName: 'Pending' }, { tabName: 'Action Taken' }]}
                getTabIndex={handleMyQueueTabs}
                defaultValue={myQueueTab}
              />
            </Grid>
          </Grid>
        )}
      </Grid>
    </Card>
  );
};

export default Header;
